import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Award, Save, Loader2, Settings, Info, Clock, Bell } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLoyalty = () => {
  const { getAuthHeader } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    isActive: true,
    pointsValidityDays: 365
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/loyalty/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch loyalty settings:', error);
      toast.error('Nie udało się pobrać ustawień');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/loyalty/settings`, settings, {
        headers: getAuthHeader()
      });
      toast.success('Ustawienia zapisane pomyślnie');
    } catch (error) {
      console.error('Failed to save loyalty settings:', error);
      toast.error('Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Award className="w-7 h-7 text-yellow-500" />
            Program Lojalnościowy
          </h1>
          <p className="text-gray-400 mt-1">
            Zarządzaj ustawieniami programu lojalnościowego
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Zapisz ustawienia
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-gray-300 text-sm">
            <p>Program lojalnościowy pozwala nagradzać klientów punktami za zakupy.</p>
            <p className="text-gray-500 text-xs mt-1">
              Punkty za poszczególne produkty ustawiasz w zakładce "Tłumaczenia FitNet" przy każdym karnecie lub typie sesji.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Settings */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Główne ustawienia
          </CardTitle>
          <CardDescription>
            Podstawowa konfiguracja programu lojalnościowego
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Active toggle */}
          <div className="flex items-center justify-between p-4 bg-black/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${settings.isActive ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                <Award className={`w-5 h-5 ${settings.isActive ? 'text-green-500' : 'text-gray-500'}`} />
              </div>
              <div>
                <p className="text-white font-medium">Program aktywny</p>
                <p className="text-sm text-gray-400">
                  {settings.isActive 
                    ? 'Punkty są naliczane za zakupy' 
                    : 'Program jest wyłączony, punkty nie są naliczane'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.isActive}
              onCheckedChange={(checked) => setSettings({ ...settings, isActive: checked })}
            />
          </div>

          {/* Validity days */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <Label className="text-white">Ważność punktów (dni)</Label>
            </div>
            <Input
              type="number"
              min="30"
              max="3650"
              value={settings.pointsValidityDays}
              onChange={(e) => setSettings({ ...settings, pointsValidityDays: parseInt(e.target.value) || 365 })}
              className="w-48 bg-black/30 border-white/10"
            />
            <p className="text-sm text-gray-500">
              Punkty wygasają po {settings.pointsValidityDays} dniach od daty ich przyznania.
              Domyślnie: 365 dni (1 rok).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Reminders Info */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Przypomnienia email
          </CardTitle>
          <CardDescription>
            Automatyczne powiadomienia o wygasających punktach
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-400 font-medium flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Automatyczne przypomnienia
            </p>
            <p className="text-sm text-gray-300 mt-2">
              System automatycznie wysyła email do klientów <strong>30 dni przed wygaśnięciem</strong> ich punktów.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Szablon emaila można edytować w zakładce "Szablony email" (typ: loyalty_points_expiring).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-black/20 rounded-lg">
              <p className="text-gray-400">Wygasanie punktów</p>
              <p className="text-white font-medium">Codziennie o 01:00</p>
              <p className="text-xs text-gray-500 mt-1">Automatyczne usuwanie wygasłych punktów</p>
            </div>
            <div className="p-3 bg-black/20 rounded-lg">
              <p className="text-gray-400">Wysyłka przypomnień</p>
              <p className="text-white font-medium">Codziennie o 11:00</p>
              <p className="text-xs text-gray-500 mt-1">30 dni przed wygaśnięciem punktów</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Points Configuration Link */}
      <Card className="bg-purple-500/10 border-purple-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-400 font-medium">Konfiguracja punktów za produkty</p>
              <p className="text-sm text-gray-400 mt-1">
                Ustaw ile punktów klient otrzymuje za zakup poszczególnych karnetów i sesji
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/admin/fitnet-translations'}
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
            >
              Przejdź do tłumaczeń FitNet
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoyalty;
